import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Phone, Mail, MapPin, Inbox, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { LogoMark } from "../Logo";
import { STATUS, SERVICE, fmt } from "./constants";
import { QuoteDetails } from "./QuoteDetails";

const st = (s) => STATUS[s] || STATUS.new;

const QuoteCard = ({ q, onStatus, onView }) => (
  <article className="rounded-2xl border border-emerald-500/15 bg-[#0C1210] p-5 sm:p-6" data-testid={`quote-card-${q.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-xl font-extrabold text-white">{q.name}</h3>
        <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">{fmt(q.created_at)} · {SERVICE[q.service] || q.service}</p>
      </div>
      <span className={`rounded-full px-3 py-1 text-xs font-bold ${st(q.status).cls}`} data-testid={`quote-status-badge-${q.id}`}>{st(q.status).label}</span>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
      <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-[#00E676]"><Phone className="h-4 w-4" />{q.phone}</a>
      {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 break-all text-zinc-200"><Mail className="h-4 w-4" />{q.email}</a>}
      <span className="flex items-center gap-2 text-zinc-300"><MapPin className="h-4 w-4" />{q.area}</span>
    </div>
    <p className="mt-4 line-clamp-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{q.message}</p>
    <div className="mt-4 grid grid-cols-2 gap-2 sm:flex">
      <a href={`tel:${q.phone}`} className="btn-primary justify-center" data-testid={`quote-call-btn-${q.id}`}><Phone className="h-4 w-4" />Κλήση</a>
      <button onClick={() => onView(q)} className="btn-ghost justify-center" data-testid={`quote-view-btn-${q.id}`}><Eye className="h-4 w-4" />Προβολή</button>
    </div>
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/5 pt-4">
      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS).map(([k, v]) => (
          <button key={k} onClick={() => onStatus(q.id, k)} disabled={q.status === k} data-testid={`quote-set-${k}-${q.id}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${q.status === k ? "border-transparent " + v.cls : "border-emerald-500/25 text-zinc-300 hover:border-emerald-400 hover:text-white"}`}>
            {v.label}
          </button>
        ))}
      </div>
      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">email: {q.email_status === "sent" ? "✓ εστάλη" : q.email_status === "failed" ? "✗ απέτυχε" : "…"}</span>
    </div>
  </article>
);

export const Dashboard = ({ user, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewId, setViewId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης αιτημάτων");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      toast.success(`Κατάσταση: ${STATUS[status].label}`);
    } catch (e) {
      toast.error("Αποτυχία ενημέρωσης");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Οριστική διαγραφή του αιτήματος και όλων των στοιχείων του πελάτη;")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setViewId(null);
      toast.success("Το αίτημα διαγράφηκε");
    } catch (e) {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const counts = useMemo(() => ({ all: quotes.length, ...Object.fromEntries(Object.keys(STATUS).map((k) => [k, quotes.filter((q) => q.status === k).length])) }), [quotes]);
  const shown = quotes.filter((q) => (filter === "all" || q.status === filter) &&
    `${q.name} ${q.phone} ${q.area} ${q.email || ""}`.toLowerCase().includes(search.toLowerCase()));
  const tabs = [["all", "Όλα"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])];

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-8 sm:py-10" data-testid="admin-dashboard">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3"><LogoMark className="h-10 w-10" /><div><p className="eyebrow">KORANI GROUP</p><p className="text-xs text-zinc-400">{user.email}</p></div></div>
        <div className="flex gap-2">
          <button onClick={load} className="grid h-10 w-10 place-items-center rounded-full border border-emerald-500/25 hover:border-emerald-400" data-testid="admin-refresh-btn" aria-label="refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={onLogout} className="flex h-10 items-center gap-2 rounded-full border border-emerald-500/25 px-4 text-sm hover:border-emerald-400" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Αποσύνδεση</span></button>
        </div>
      </header>
      <h1 className="mt-10 font-display text-3xl font-black uppercase sm:text-5xl">Αιτήματα <span className="text-[#00E676]">πελατών</span></h1>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
            className={`rounded-2xl border p-4 text-left transition-colors ${filter === k ? "border-[#00E676] bg-[#00E676]/10" : "border-emerald-500/15 bg-[#0C1210] hover:border-emerald-500/40"}`}>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">{label}</p>
            <p className="mt-1 font-display text-3xl font-black text-white" data-testid={`admin-count-${k}`}>{counts[k]}</p>
          </button>
        ))}
      </div>
      <div className="relative mt-6">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <input className="input" style={{ paddingLeft: 44 }} placeholder="Αναζήτηση ονόματος, τηλεφώνου, περιοχής..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-search-input" />
      </div>
      <div className="mt-6 space-y-4" data-testid="admin-quotes-list">
        {shown.map((q) => <QuoteCard key={q.id} q={q} onStatus={onStatus} onView={(x) => setViewId(x.id)} />)}
        {!loading && shown.length === 0 && (
          <div className="grid place-items-center rounded-2xl border border-dashed border-emerald-500/20 py-16 text-zinc-400" data-testid="admin-empty"><Inbox className="h-8 w-8" /><p className="mt-3">Δεν υπάρχουν αιτήματα</p></div>
        )}
      </div>
      <QuoteDetails q={quotes.find((x) => x.id === viewId)} onClose={() => setViewId(null)} onStatus={onStatus} onDelete={onDelete} />
    </div>
  );
};
